// Global Instructions Rule Applied!
//
// Security full-form: the security's identity + key commercial terms,
// editable in place (issuer is immutable — moving a security to another
// company under existing tranches would corrupt the holdings), plus the
// tranches holding the security with their current per-fund positions.
// Percent fields display as percentages and store as decimals (0.05 = 5%),
// matching the "+ New security" modal on the booking form.
//
// Interest terms (docs/INTEREST_ACCRUAL_DESIGN.md section 6.4, PR A): a
// security type that uses interest gets its own section - rate, rate type,
// compounding and its first period end, day count, payment type, accrual
// start, whether it keeps accruing past maturity - and a table of rate
// periods (a default rate after maturity, a stepped coupon) edited in place
// as part of the form's draft and saved with it as a full replacement set
// (the CommitmentAllocationSection precedent: antd inputs in the cells, no
// per-row Save). The terms drive at__interest_periods, the engine behind the
// Tranche form's accrued interest.
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Dropdown, Input, InputNumber, Modal, Select, Spin, Switch, Table } from 'antd'
import DatePicker from '../../../core/components/DatePicker.tsx'
import { DeleteOutlined, MoreOutlined, PlusOutlined } from '@ant-design/icons'
import { FormSection } from '../../../core/components/FormSection.tsx'
import AddSecurityModal from '../../asset_transactions/components/AddSecurityModal.tsx'
import { ChangeLogTab } from '../../change_log/components/ChangeLogTab.tsx'
import ExportRowsButton from '../../shared/table_grid_export/ExportRowsButton.tsx'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { updateTabTitle } from '../../../store/slices/tabSlice'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { useNumberFormat, decimalToPercent, percentToDecimal } from '../../../core/utils/inputNumberFormat.ts'
import { deleteSecurity, fetchSecurityDetail, saveSecurity } from '../services/securitiesService.ts'
import {
  SecurityDetailPayload,
  SecurityTrancheEntry
} from '../../../state_manager/data_models/security_detail_model.ts'

const DATE_FORMAT = 'MM/DD/YYYY'

// a rate period row of the form's draft (rate held as a percentage)
interface RatePeriodDraft {
  key: string
  id: string | null
  effective_from: string | null
  interest_rate: number | null
  day_count_convention_id: number | null
}

interface FullSecurityFormProps {
  recordId?: string | null
  isNew?: boolean
}

function Row({ label, children }: { label: string; children: React.ReactNode }): React.ReactElement {
  return (
    <div className='flex items-center gap-3 py-1'>
      <span className='w-40 flex-none text-xs text-gray-500'>{label}</span>
      <div className='flex-1 min-w-0 text-sm'>{children}</div>
    </div>
  )
}

// House record-link styling (same as the deal-execution tables' recordLink).
function recordLink(label: string, onClick: () => void): React.ReactElement {
  return (
    <button
      type='button'
      className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
      onClick={onClick}
    >
      {label}
    </button>
  )
}

// House form ergonomics: Enter commits the field (blurs) instead of leaving
// the cursor trapped in the input.
const blurOnEnter = (e: React.KeyboardEvent) => {
  if (e.key === 'Enter') (e.target as HTMLElement).blur()
}

function money(symbol: string | null | undefined, value: number | null | undefined): string {
  if (value == null) return '—'
  return `${symbol || ''}${Number(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`
}

function count(value: number | null | undefined): string {
  if (value == null) return '—'
  return Number(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

function unitValue(symbol: string | null | undefined, value: number | null | undefined): string {
  if (value == null) return '—'
  return `${symbol || ''}${Number(value).toLocaleString('en-US', { maximumFractionDigits: 4 })}`
}

export default function FullSecurityForm({ recordId }: FullSecurityFormProps): React.ReactElement {
  const { openFormInTab, closeFormTab } = useTabManagerContext()
  const dispatch = useDispatch()
  const tabs = useSelector(
    (state: { tabs?: { tabs?: Array<{ key: string; module?: string; recordId?: string; title?: string }> } }) =>
      state.tabs?.tabs || []
  )
  const securityTypes = useSelector((state: any) => state.globalConfigs?.security_types ?? [])
  const valuationTypes = useSelector((state: any) => state.globalConfigs?.asset_valuation_types ?? [])
  const currencies = useSelector((state: any) => state.globalConfigs?.currencies ?? [])
  const valuationCapTypes = useSelector((state: any) => state.globalConfigs?.valuation_cap_types ?? [])
  const interestRateTypes = useSelector((state: any) => state.globalConfigs?.interest_rate_types ?? [])
  const compoundingFrequencies = useSelector(
    (state: any) => state.globalConfigs?.compounding_frequencies ?? []
  )
  const dayCountConventions = useSelector((state: any) => state.globalConfigs?.day_count_conventions ?? [])
  const interestPaymentTypes = useSelector((state: any) => state.globalConfigs?.interest_payment_types ?? [])

  const currencyFmt = useNumberFormat('currency')
  const unitPriceFmt = useNumberFormat('unit_price')

  const [payload, setPayload] = useState<SecurityDetailPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dirty, setDirty] = useState(false)
  // Read-only until Edit is clicked (owner request 2026-07-27)
  const [editing, setEditing] = useState(false)
  // "New Security" (owner request 2026-07-28): same modal as the booking
  // form's "+ New security", with an Issuer select since there is no company
  // context here. On create, the new security opens in its own tab.
  const [addOpen, setAddOpen] = useState(false)
  const [modal, modalContextHolder] = Modal.useModal()
  const formTabKey = `securities-form-edit-${recordId}`

  // Editable fields (percent fields held as percentages, saved as decimals)
  const [name, setName] = useState('')
  const [typeId, setTypeId] = useState<number | null>(null)
  const [valuationTypeId, setValuationTypeId] = useState<number | null>(null)
  const [currencyId, setCurrencyId] = useState<string | null>(null)
  const [issuePrice, setIssuePrice] = useState<number | null>(null)
  const [nominalValue, setNominalValue] = useState<number | null>(null)
  const [interestRate, setInterestRate] = useState<number | null>(null)
  const [interestRateTypeId, setInterestRateTypeId] = useState<number | null>(null)
  const [compoundingFrequencyId, setCompoundingFrequencyId] = useState<number | null>(null)
  const [dayCountConventionId, setDayCountConventionId] = useState<number | null>(null)
  const [maturityDate, setMaturityDate] = useState<string | null>(null)
  const [maturityMonths, setMaturityMonths] = useState<number | null>(null)
  // Interest terms beyond the rate itself (see the file header)
  const [firstPeriodEndDate, setFirstPeriodEndDate] = useState<string | null>(null)
  const [accruesAfterMaturity, setAccruesAfterMaturity] = useState<boolean>(true)
  const [interestAccruesFrom, setInterestAccruesFrom] = useState<string | null>(null)
  const [interestPaymentTypeId, setInterestPaymentTypeId] = useState<number | null>(null)
  const [ratePeriods, setRatePeriods] = useState<RatePeriodDraft[]>([])
  const [valuationCapTypeId, setValuationCapTypeId] = useState<number | null>(null)
  const [valuationCap, setValuationCap] = useState<number | null>(null)
  const [valuationFloor, setValuationFloor] = useState<number | null>(null)
  const [conversionDiscount, setConversionDiscount] = useState<number | null>(null)
  const [strikePrice, setStrikePrice] = useState<number | null>(null)
  // FD-common-stock equivalent per unit — drives overall our-holding tiles
  // (cap table / legal rights). Defaults from cfg.security_types on type
  // change; NULL = excluded from FD-equivalent holdings (e.g. unconverted SAFE).
  const [fdCommonEquivalent, setFdCommonEquivalent] = useState<number | null>(null)
  const [notes, setNotes] = useState('')

  // ----- cfg-driven field visibility (flags on cfg.security_types; pattern:
  // the asset-transaction booking form). A type with NO flags set (Loan,
  // Token, ...) keeps the legacy full field set until the owner assigns
  // flags. Cap/floor further depend on the chosen cfg.valuation_cap_types
  // row; compounding frequency on the interest-rate type's `compounds`.
  const selType = securityTypes.find((t: any) => t.id === typeId)
  const anyFlag = Boolean(
    selType &&
      (selType.is_convertible ||
        selType.uses_interest ||
        selType.uses_nominal ||
        selType.uses_strike ||
        selType.uses_issue_price ||
        selType.uses_maturity)
  )
  const showAll = !anyFlag
  const showConvertible = Boolean(selType?.is_convertible)
  const usesInterest = Boolean(selType?.uses_interest)
  const usesMaturity = Boolean(selType?.uses_maturity)
  const capType = valuationCapTypes.find((v: any) => v.id === valuationCapTypeId)
  const irType = interestRateTypes.find((r: any) => r.id === interestRateTypeId)

  // Applicability per the cfg flags…
  const issuePriceApplies = showAll || Boolean(selType?.uses_issue_price)
  const nominalApplies = showAll || Boolean(selType?.uses_nominal)
  const strikeApplies = showAll || Boolean(selType?.uses_strike)
  const interestApplies = showAll || usesInterest
  const compoundingApplies = showAll || (usesInterest && Boolean(irType?.compounds))
  const maturityApplies = showAll || usesMaturity
  const maturityMonthsApplies = showAll || (usesMaturity && showConvertible)
  const convertibleApplies = showAll || showConvertible
  const capApplies = showAll || (showConvertible && Boolean(capType?.has_cap))
  const floorApplies = showAll || (showConvertible && Boolean(capType?.has_floor))

  // …but a term the security ALREADY carries is always shown, even when the
  // flags say it shouldn't apply (a note with a cap but no cap type recorded
  // is real data — hiding it made the form both lie about the security AND,
  // before saves went key-presence-partial, silently null the hidden value on
  // the next save). Same rule as the Round Securities card.
  const has = (v: number | string | null | undefined): boolean => v != null && v !== ''
  const showIssuePrice = issuePriceApplies || has(issuePrice)
  const showNominal = nominalApplies || has(nominalValue)
  const showCapType = convertibleApplies || has(valuationCapTypeId)
  const showCap = capApplies || has(valuationCap)
  const showFloor = floorApplies || has(valuationFloor)
  const showDiscount = convertibleApplies || has(conversionDiscount)
  const showInterest =
    interestApplies || has(interestRate) || has(interestRateTypeId) || ratePeriods.length > 0
  const showCompounding = compoundingApplies || has(compoundingFrequencyId)
  const showMaturity = maturityApplies || has(maturityDate)
  const showMaturityMonths = maturityMonthsApplies || has(maturityMonths)
  const showStrike = strikeApplies || has(strikePrice)
  // Currency amounts carry the selected currency's symbol (booking-form idiom)
  const ccySymbol = currencies.find((c: any) => c.id === currencyId)?.symbol || '$'

  const initFromPayload = (p: SecurityDetailPayload) => {
    const s = p.security
    setName(s.securities__name || '')
    setTypeId(s.cfg_security_types__id ?? null)
    setValuationTypeId(s.cfg_asset_valuation_types__id ?? null)
    setCurrencyId(s.currencies__id ?? null)
    setIssuePrice(s.securities__issue_price ?? null)
    setNominalValue(s.securities__nominal_value ?? null)
    setInterestRate(decimalToPercent(s.securities__interest_rate))
    setInterestRateTypeId(s.cfg_interest_rate_types__id ?? null)
    setCompoundingFrequencyId(s.cfg_compounding_frequencies__id ?? null)
    setDayCountConventionId(s.cfg_day_count_conventions__id ?? null)
    setMaturityDate(s.securities__maturity_date ?? null)
    setMaturityMonths(s.securities__maturity_months ?? null)
    setFirstPeriodEndDate(s.securities__first_period_end_date ?? null)
    setAccruesAfterMaturity(s.securities__accrues_after_maturity ?? true)
    setInterestAccruesFrom(s.securities__interest_accrues_from ?? null)
    setInterestPaymentTypeId(s.cfg_interest_payment_types__id ?? null)
    setRatePeriods(
      (p.rate_periods ?? []).map((rp) => ({
        key: rp.security_interest_rate_periods__id,
        id: rp.security_interest_rate_periods__id,
        effective_from: rp.security_interest_rate_periods__effective_from,
        interest_rate: decimalToPercent(rp.security_interest_rate_periods__interest_rate),
        day_count_convention_id: rp.cfg_day_count_conventions__id ?? null
      }))
    )
    setValuationCapTypeId(s.cfg_valuation_cap_types__id ?? null)
    setValuationCap(s.securities__valuation_cap ?? null)
    setValuationFloor(s.securities__valuation_floor ?? null)
    setConversionDiscount(decimalToPercent(s.securities__conversion_discount))
    setStrikePrice(s.securities__strike_price ?? null)
    setFdCommonEquivalent(s.securities__fd_common_equivalent ?? null)
    setNotes(s.securities__notes || '')
    setDirty(false)
  }

  const load = async (cancelledRef?: { current: boolean }) => {
    if (!recordId) return
    setLoading(true)
    const token = getAccessToken(store.getState().auth)
    const result = await fetchSecurityDetail(token, recordId)
    if (cancelledRef?.current) return
    setPayload(result.data)
    setError(result.error || null)
    if (result.data) initFromPayload(result.data)
    setLoading(false)
  }

  useEffect(() => {
    const cancelledRef = { current: false }
    load(cancelledRef)
    return () => {
      cancelledRef.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordId])

  // Tab titles are slice-derived per module in MainTabContainer; securities
  // have no slice, so the form names its own tab once the record loads.
  useEffect(() => {
    const tabName = payload?.security?.securities__name
    if (!tabName) return
    const tab = tabs.find((t) => t.module === 'securities' && t.recordId === recordId)
    if (tab && tab.title !== tabName) {
      dispatch(updateTabTitle({ key: tab.key, title: tabName }))
    }
  }, [payload, tabs, recordId, dispatch])

  const handleSave = async () => {
    if (!recordId || !payload) return
    if (!name.trim() || !typeId || !valuationTypeId || !currencyId) {
      showErrorToast('Name, security type, valuation type and currency are required')
      return
    }
    if (showInterest && ratePeriods.some((rp) => !rp.effective_from || rp.interest_rate == null)) {
      showErrorToast('Every rate period needs an effective date and a rate')
      return
    }
    setSaving(true)
    try {
      const token = getAccessToken(store.getState().auth)
      // A security-TYPE change is what clears now-inapplicable terms —
      // switching a SAFE to Common Stock must not carry a stale cap. A term
      // that is merely hidden by display logic (not applicable but also not
      // shown, e.g. nothing stored) is OMITTED, and the partial-payload save
      // keeps its stored value: the old "hidden saves as NULL" rule silently
      // wiped real terms (a Convertible Note with no cap type recorded lost
      // its cap on every save). A term that is visible saves whatever the
      // operator left in it, including a deliberate clear.
      const typeChanged = typeId !== (payload.security.cfg_security_types__id ?? null)
      const term = <T,>(applies: boolean, visible: boolean, value: T): T | null | undefined =>
        typeChanged && !applies ? null : visible ? value : undefined
      const res = await saveSecurity(token, recordId, {
        name: name.trim(),
        type_id: typeId,
        valuation_type_id: valuationTypeId,
        currency_id: currencyId,
        issue_price: term(issuePriceApplies, showIssuePrice, issuePrice),
        nominal_value: term(nominalApplies, showNominal, nominalValue),
        // entered as percentages, stored as decimals (0.05 = 5%)
        interest_rate: term(interestApplies, showInterest, percentToDecimal(interestRate)),
        interest_rate_type_id: term(interestApplies, showInterest, interestRateTypeId),
        compounding_frequency_id: term(interestApplies, showCompounding, compoundingFrequencyId),
        day_count_convention_id: term(interestApplies, showInterest, dayCountConventionId),
        maturity_date: term(maturityApplies, showMaturity, maturityDate),
        maturity_months: term(maturityMonthsApplies, showMaturityMonths, maturityMonths),
        valuation_cap_type_id: term(convertibleApplies, showCapType, valuationCapTypeId),
        valuation_cap: term(convertibleApplies, showCap, valuationCap),
        valuation_floor: term(convertibleApplies, showFloor, valuationFloor),
        conversion_discount: term(convertibleApplies, showDiscount, percentToDecimal(conversionDiscount)),
        strike_price: term(strikeApplies, showStrike, strikePrice),
        fd_common_equivalent: fdCommonEquivalent,
        notes: notes.trim() ? notes.trim() : null,
        first_period_end_date: term(interestApplies, showCompounding, firstPeriodEndDate),
        accrues_after_maturity: showInterest ? accruesAfterMaturity : undefined,
        interest_accrues_from: term(interestApplies, showInterest, interestAccruesFrom),
        interest_payment_type_id: term(interestApplies, showInterest, interestPaymentTypeId),
        // the full set: a type switch away from interest clears them
        rate_periods:
          typeChanged && !interestApplies
            ? []
            : showInterest
              ? ratePeriods.map((rp) => ({
                  id: rp.id,
                  effective_from: rp.effective_from as string,
                  // entered as percentages, stored as decimals
                  interest_rate: percentToDecimal(rp.interest_rate) as number,
                  day_count_convention_id: rp.day_count_convention_id
                }))
              : undefined
      })
      if (!res.success) {
        showErrorToast(res.error || 'Failed to save security')
        return
      }
      toast.success('Security saved')
      await load()
      setEditing(false)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className='flex justify-center py-16'>
        <Spin />
      </div>
    )
  }

  if (!payload || error) {
    return (
      <div className='max-w-xl mx-auto text-center py-16 text-gray-500 text-sm'>
        {error || 'Security not found.'}
      </div>
    )
  }

  const s = payload.security
  const markDirty = () => setDirty(true)

  const handleDelete = () => {
    modal.confirm({
      title: 'Delete this security?',
      content:
        'The security will be moved to trash. Deletion is refused while tranches, transactions or valuations still reference it.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        if (!recordId) return
        const token = getAccessToken(store.getState().auth)
        const res = await deleteSecurity(token, recordId)
        if (!res.success) {
          showErrorToast(res.error || 'Failed to delete security')
          return
        }
        toast.success('Security deleted')
        closeFormTab(formTabKey)
      }
    })
  }

  return (
    <div className='h-full overflow-y-auto px-4 py-4'>
      {modalContextHolder}
      <AddSecurityModal
        open={addOpen}
        companyId={null}
        onCreated={(securityId) => {
          setAddOpen(false)
          openFormInTab('securities', securityId, 'Security')
        }}
        onCancel={() => setAddOpen(false)}
      />
      <div className='flex items-center gap-2 mb-4'>
        <h2 className='text-base font-semibold m-0'>{s.securities__name || 'Security'}</h2>
        <div className='flex-1' />
        {editing ? (
          <>
            <Button
              disabled={saving}
              onClick={() => {
                initFromPayload(payload)
                setEditing(false)
              }}
            >
              Cancel
            </Button>
            <Button type='primary' disabled={!dirty} loading={saving} onClick={handleSave}>
              Save
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => setEditing(true)}>Edit</Button>
            {/* House new-record convention: PlusOutlined + bare noun,
                matching the grids' "+ Transaction" / "+ Capital Call". */}
            <Button type='primary' icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
              Security
            </Button>
          </>
        )}
        <Dropdown
          trigger={['click']}
          menu={{
            items: [
              {
                key: 'delete',
                label: 'Delete',
                icon: <DeleteOutlined />,
                danger: true,
                onClick: handleDelete
              }
            ]
          }}
        >
          <Button icon={<MoreOutlined />} />
        </Dropdown>
      </div>

      <div className='grid grid-cols-1 xl:grid-cols-2 gap-4'>
        <FormSection title='Security Details' scrollable={false}>
          <Row label='Name'>
            <Input
              value={name}
              disabled={!editing}
              onKeyDown={blurOnEnter}
              onChange={(e) => {
                setName(e.target.value)
                markDirty()
              }}
            />
          </Row>
          <Row label='Issuer'>
            {/* Immutable (re-issuing under existing tranches would corrupt
                holdings) — shown in the standard disabled-input box, with the
                value itself staying a record link to the company. */}
            <div className='flex items-center min-h-[32px] rounded-md border border-[#d9d9d9] bg-black/[0.04] px-[11px]'>
              {s.accounts__id
                ? recordLink(s.accounts__name || 'Company', () =>
                    openFormInTab('companies', s.accounts__id as string, s.accounts__name || 'Company')
                  )
                : s.accounts__name || '—'}
            </div>
          </Row>
          <Row label='Security type'>
            <Select
              className='w-full'
              disabled={!editing}
              value={typeId}
              options={securityTypes.map((t: any) => ({ value: t.id, label: t.label }))}
              showSearch
              optionFilterProp='label'
              onChange={(v) => {
                setTypeId(v)
                const t = securityTypes.find((x: any) => x.id === v)
                if (t?.default_valuation_type_id != null) setValuationTypeId(t.default_valuation_type_id)
                // Type switch resets the FD equivalent to the new type's cfg
                // default (same no-stale-terms rule as the hidden fields).
                setFdCommonEquivalent(t?.default_fd_common_equivalent ?? null)
                markDirty()
              }}
            />
          </Row>
          <Row label='Valuation type'>
            <Select
              className='w-full'
              disabled={!editing}
              value={valuationTypeId}
              options={valuationTypes.map((v: any) => ({ value: v.id, label: v.label }))}
              onChange={(v) => {
                setValuationTypeId(v ?? null)
                markDirty()
              }}
            />
          </Row>
          <Row label='Currency'>
            <Select
              className='w-full'
              disabled={!editing}
              value={currencyId}
              options={currencies.map((c: any) => ({ value: c.id, label: c.code }))}
              showSearch
              optionFilterProp='label'
              onChange={(v) => {
                setCurrencyId(v ?? null)
                markDirty()
              }}
            />
          </Row>
          <Row label='Notes'>
            <Input.TextArea
              value={notes}
              disabled={!editing}
              autoSize={{ minRows: 2, maxRows: 6 }}
              onChange={(e) => {
                setNotes(e.target.value)
                markDirty()
              }}
            />
          </Row>
        </FormSection>

        <FormSection title='Key Terms' scrollable={false}>
          {showIssuePrice ? (
            <Row label='Issue price'>
              <InputNumber
                className='w-full'
                disabled={!editing}
                value={issuePrice}
                prefix={ccySymbol}
                {...unitPriceFmt}
                onKeyDown={blurOnEnter}
                onChange={(v) => {
                  setIssuePrice(v == null ? null : Number(v))
                  markDirty()
                }}
              />
            </Row>
          ) : null}
          <Row label='FD common equivalent'>
            <InputNumber
              className='w-full'
              disabled={!editing}
              value={fdCommonEquivalent}
              min={0}
              placeholder='Excluded from FD-equivalent holdings'
              onKeyDown={blurOnEnter}
              onChange={(v) => {
                setFdCommonEquivalent(v == null ? null : Number(v))
                markDirty()
              }}
            />
          </Row>
          {showCapType ? (
            <Row label='Valuation cap type'>
              <Select
                className='w-full'
                disabled={!editing}
                value={valuationCapTypeId}
                allowClear
                // id order IS the display order (hand-assigned cfg ids)
                options={valuationCapTypes.map((v: any) => ({ value: v.id, label: v.label }))}
                onChange={(v) => {
                  setValuationCapTypeId(v ?? null)
                  markDirty()
                }}
              />
            </Row>
          ) : null}
          {showCap ? (
            <Row label='Valuation cap'>
              <InputNumber
                className='w-full'
                disabled={!editing}
                value={valuationCap}
                prefix={ccySymbol}
                {...currencyFmt}
                onKeyDown={blurOnEnter}
                onChange={(v) => {
                  setValuationCap(v == null ? null : Number(v))
                  markDirty()
                }}
              />
            </Row>
          ) : null}
          {showFloor ? (
            <Row label='Valuation floor'>
              <InputNumber
                className='w-full'
                disabled={!editing}
                value={valuationFloor}
                prefix={ccySymbol}
                {...currencyFmt}
                onKeyDown={blurOnEnter}
                onChange={(v) => {
                  setValuationFloor(v == null ? null : Number(v))
                  markDirty()
                }}
              />
            </Row>
          ) : null}
          {showDiscount ? (
            <Row label='Discount %'>
              <InputNumber
                className='w-full'
                disabled={!editing}
                value={conversionDiscount}
                suffix='%'
                onKeyDown={blurOnEnter}
                onChange={(v) => {
                  setConversionDiscount(v == null ? null : Number(v))
                  markDirty()
                }}
              />
            </Row>
          ) : null}
          {showNominal ? (
            <Row label='Nominal value'>
              <InputNumber
                className='w-full'
                disabled={!editing}
                value={nominalValue}
                prefix={ccySymbol}
                {...unitPriceFmt}
                onKeyDown={blurOnEnter}
                onChange={(v) => {
                  setNominalValue(v == null ? null : Number(v))
                  markDirty()
                }}
              />
            </Row>
          ) : null}
          {showMaturity ? (
            <Row label='Maturity date'>
              <DatePicker
                className='w-full'
                disabled={!editing}
                value={maturityDate ? dayjs(maturityDate) : null}
                format={DATE_FORMAT}
                onChange={(d) => {
                  setMaturityDate(d ? d.format('YYYY-MM-DD') : null)
                  markDirty()
                }}
              />
            </Row>
          ) : null}
          {showMaturityMonths ? (
            <Row label='Maturity (months)'>
              <InputNumber
                className='w-full'
                disabled={!editing}
                value={maturityMonths}
                min={0}
                precision={0}
                onKeyDown={blurOnEnter}
                onChange={(v) => {
                  setMaturityMonths(v == null ? null : Number(v))
                  markDirty()
                }}
              />
            </Row>
          ) : null}
          {showStrike ? (
            <Row label='Strike price'>
              <InputNumber
                className='w-full'
                disabled={!editing}
                value={strikePrice}
                prefix={ccySymbol}
                {...unitPriceFmt}
                onKeyDown={blurOnEnter}
                onChange={(v) => {
                  setStrikePrice(v == null ? null : Number(v))
                  markDirty()
                }}
              />
            </Row>
          ) : null}
        </FormSection>
      </div>

      {showInterest ? (
        <FormSection
          title='Interest terms'
          scrollable={false}
          className='mt-4'
          headerActions={
            editing ? (
              <Button
                size='small'
                icon={<PlusOutlined />}
                onClick={() => {
                  setRatePeriods((rows) => [
                    ...rows,
                    {
                      key: `new-${Date.now()}-${rows.length}`,
                      id: null,
                      effective_from: null,
                      interest_rate: null,
                      day_count_convention_id: null
                    }
                  ])
                  markDirty()
                }}
              >
                Rate period
              </Button>
            ) : null
          }
        >
          <div className='grid grid-cols-1 xl:grid-cols-2 gap-x-6'>
            <div>
              <Row label='Interest rate %'>
                <InputNumber
                  className='w-full'
                  disabled={!editing}
                  value={interestRate}
                  suffix='%'
                  onKeyDown={blurOnEnter}
                  onChange={(v) => {
                    setInterestRate(v == null ? null : Number(v))
                    markDirty()
                  }}
                />
              </Row>
              <Row label='Interest rate type'>
                <Select
                  className='w-full'
                  disabled={!editing}
                  value={interestRateTypeId}
                  allowClear
                  placeholder='Simple'
                  options={interestRateTypes.map((r: any) => ({ value: r.id, label: r.label }))}
                  onChange={(v) => {
                    setInterestRateTypeId(v ?? null)
                    markDirty()
                  }}
                />
              </Row>
              {showCompounding ? (
                <Row label='Compounding'>
                  <Select
                    className='w-full'
                    disabled={!editing}
                    value={compoundingFrequencyId}
                    allowClear
                    options={compoundingFrequencies
                      .filter((c: any) => !c.once_off)
                      .map((c: any) => ({ value: c.id, label: c.label }))}
                    onChange={(v) => {
                      setCompoundingFrequencyId(v ?? null)
                      markDirty()
                    }}
                  />
                </Row>
              ) : null}
              {showCompounding ? (
                <Row label='First period end'>
                  <DatePicker
                    className='w-full'
                    disabled={!editing}
                    value={firstPeriodEndDate ? dayjs(firstPeriodEndDate) : null}
                    format={DATE_FORMAT}
                    placeholder='Anniversaries of the accrual start'
                    onChange={(d) => {
                      setFirstPeriodEndDate(d ? d.format('YYYY-MM-DD') : null)
                      markDirty()
                    }}
                  />
                </Row>
              ) : null}
              <Row label='Day count convention'>
                <Select
                  className='w-full'
                  disabled={!editing}
                  value={dayCountConventionId}
                  allowClear
                  placeholder='Actual / 365'
                  options={dayCountConventions.map((d: any) => ({ value: d.id, label: d.label }))}
                  onChange={(v) => {
                    setDayCountConventionId(v ?? null)
                    markDirty()
                  }}
                />
              </Row>
            </div>
            <div>
              <Row label='Payment'>
                <Select
                  className='w-full'
                  disabled={!editing}
                  value={interestPaymentTypeId}
                  allowClear
                  options={interestPaymentTypes.map((t: any) => ({ value: t.id, label: t.label }))}
                  onChange={(v) => {
                    setInterestPaymentTypeId(v ?? null)
                    markDirty()
                  }}
                />
              </Row>
              <Row label='Accrues from'>
                <DatePicker
                  className='w-full'
                  disabled={!editing}
                  value={interestAccruesFrom ? dayjs(interestAccruesFrom) : null}
                  format={DATE_FORMAT}
                  placeholder="Each tranche's acquisition date"
                  onChange={(d) => {
                    setInterestAccruesFrom(d ? d.format('YYYY-MM-DD') : null)
                    markDirty()
                  }}
                />
              </Row>
              <Row label='Accrues after maturity'>
                <Switch
                  size='small'
                  disabled={!editing}
                  checked={accruesAfterMaturity}
                  aria-label='Accrues after maturity'
                  onChange={(v) => {
                    setAccruesAfterMaturity(v)
                    markDirty()
                  }}
                />
              </Row>
            </div>
          </div>
          <Table<RatePeriodDraft>
            size='small'
            className='mt-2'
            rowKey='key'
            pagination={false}
            dataSource={ratePeriods}
            locale={{ emptyText: 'No rate periods: the rate above applies throughout' }}
            columns={[
              {
                title: 'Effective from',
                dataIndex: 'effective_from',
                width: 180,
                render: (v, row) =>
                  editing ? (
                    <DatePicker
                      size='small'
                      className='w-full'
                      value={v ? dayjs(String(v)) : null}
                      format={DATE_FORMAT}
                      aria-label='Rate period effective from'
                      onChange={(d) => {
                        setRatePeriods((rows) =>
                          rows.map((r) => (r.key === row.key ? { ...r, effective_from: d ? d.format('YYYY-MM-DD') : null } : r))
                        )
                        markDirty()
                      }}
                    />
                  ) : v ? (
                    dayjs(String(v)).format(DATE_FORMAT)
                  ) : (
                    '—'
                  )
              },
              {
                title: 'Rate',
                dataIndex: 'interest_rate',
                width: 140,
                align: 'right' as const,
                render: (v, row) =>
                  editing ? (
                    <InputNumber
                      size='small'
                      className='w-full'
                      value={v as number | null}
                      suffix='%'
                      min={0}
                      aria-label='Rate period rate'
                      onKeyDown={blurOnEnter}
                      onChange={(n) => {
                        setRatePeriods((rows) =>
                          rows.map((r) => (r.key === row.key ? { ...r, interest_rate: n == null ? null : Number(n) } : r))
                        )
                        markDirty()
                      }}
                    />
                  ) : v != null ? (
                    `${Number(v).toFixed(2)}%`
                  ) : (
                    '—'
                  )
              },
              {
                title: 'Day count',
                dataIndex: 'day_count_convention_id',
                width: 200,
                render: (v, row) =>
                  editing ? (
                    <Select
                      size='small'
                      className='w-full'
                      value={(v as number | null) ?? null}
                      allowClear
                      placeholder="The security's"
                      aria-label='Rate period day count'
                      options={dayCountConventions.map((d: any) => ({ value: d.id, label: d.label }))}
                      onChange={(n) => {
                        setRatePeriods((rows) =>
                          rows.map((r) => (r.key === row.key ? { ...r, day_count_convention_id: n ?? null } : r))
                        )
                        markDirty()
                      }}
                    />
                  ) : (
                    dayCountConventions.find((d: any) => d.id === v)?.label || "The security's"
                  )
              },
              ...(editing
                ? [
                    {
                      title: '',
                      key: 'actions',
                      width: 48,
                      render: (_: unknown, row: RatePeriodDraft) => (
                        <Button
                          type='text'
                          size='small'
                          danger
                          icon={<DeleteOutlined />}
                          aria-label='Remove rate period'
                          onClick={() => {
                            setRatePeriods((rows) => rows.filter((r) => r.key !== row.key))
                            markDirty()
                          }}
                        />
                      )
                    }
                  ]
                : [])
            ]}
          />
        </FormSection>
      ) : null}

      <FormSection
        title='Tranches'
        scrollable={false}
        className='mt-4'
        headerActions={
          <ExportRowsButton
            name={`${s.securities__name || 'Security'} Tranches`}
            rows={payload.tranches}
            columns={[
              { title: 'Tranche', value: (r) => r.tranches__name },
              { title: 'Fund', value: (r) => r.funds__name },
              { title: 'Invested', value: (r) => r.prog__date_invested, kind: 'date' },
              { title: 'Exited', value: (r) => r.prog__date_exited, kind: 'date' },
              { title: 'No. Held', value: (r) => r.prog__no_held },
              { title: 'Cost Held', value: (r) => r.prog__cost_held },
              { title: 'FMV', value: (r) => r.prog__fmv },
              { title: 'Unit Value', value: (r) => r.prog__unit_price },
              { title: '% Exited', value: (r) => r.prog__perc_exited, kind: 'percent' }
            ]}
          />
        }
      >
        <Table
          size='small'
          rowKey='tranches__id'
          pagination={false}
          dataSource={payload.tranches}
          scroll={{ x: true }}
          columns={[
            {
              title: 'Tranche',
              dataIndex: 'tranches__name',
              render: (v, row: SecurityTrancheEntry) =>
                recordLink((v as string) || 'Tranche', () =>
                  openFormInTab('tranches', row.tranches__id, (v as string) || 'Tranche')
                )
            },
            {
              title: 'Fund',
              dataIndex: 'funds__name',
              render: (v, row: SecurityTrancheEntry) =>
                row.funds__id
                  ? recordLink((v as string) || 'Fund', () =>
                      openFormInTab('funds', row.funds__id as string, (v as string) || 'Fund')
                    )
                  : v || '—'
            },
            {
              title: 'Invested',
              dataIndex: 'prog__date_invested',
              render: (v) => (v ? dayjs(String(v)).format(DATE_FORMAT) : '—')
            },
            {
              title: 'Exited',
              dataIndex: 'prog__date_exited',
              render: (v) => (v ? dayjs(String(v)).format(DATE_FORMAT) : '—')
            },
            {
              title: 'No. Held',
              dataIndex: 'prog__no_held',
              align: 'right' as const,
              render: (v) => count(v)
            },
            {
              title: 'Cost Held',
              dataIndex: 'prog__cost_held',
              align: 'right' as const,
              render: (v, row: SecurityTrancheEntry) => money(row.currencies__symbol, v)
            },
            {
              title: 'FMV',
              dataIndex: 'prog__fmv',
              align: 'right' as const,
              render: (v, row: SecurityTrancheEntry) => money(row.currencies__symbol, v)
            },
            {
              title: 'Unit Value',
              dataIndex: 'prog__unit_price',
              align: 'right' as const,
              render: (v, row: SecurityTrancheEntry) => unitValue(row.currencies__symbol, v)
            },
            {
              title: '% Exited',
              dataIndex: 'prog__perc_exited',
              align: 'right' as const,
              render: (v) => (v != null ? `${(Number(v) * 100).toFixed(1)}%` : '—')
            }
          ]}
        />
      </FormSection>

      {/* This form isn't tabbed, so the shared Change Log feed mounts as a
          bounded section — its own scroll container keeps the infinite
          scroll working inside the page scroll. */}
      <FormSection title='Change Log' scrollable={false} className='mt-4'>
        <div className='h-[420px]'>
          <ChangeLogTab contextTable='securities' recordId={recordId} />
        </div>
      </FormSection>
    </div>
  )
}
